import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Plus, Pencil, Trash2, Waves, Inbox, LayoutGrid, Phone, Loader2, X, Info } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

const CATEGORIES = [
  { value: "spa", label: "Spa" },
  { value: "swim_spa", label: "Swim Spa" },
  { value: "urban_pool", label: "Urban Pool" },
  { value: "sauna", label: "Sauna" },
];
const CTA_LABEL = { interest: "Interest", info: "More info", callback: "Call back", save_build: "Saved build" };
const STATUSES = ["new", "contacted", "closed"];
const CAT_LABEL = { spa: "Spa", swim_spa: "Swim Spa", urban_pool: "Urban Pool", sauna: "Sauna" };

const emptySpa = {
  name: "", category: "spa", brand: "", tagline: "", description: "", image_url: "",
  seats: "", dimensions: "", size_label: "", zone: "", swim_system: "", config: "",
  specs: [], colours: [], upgrade_packs: [], features: [], source_url: "", sort_order: 0, active: true,
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center gap-2 text-slate-400"><Icon size={15} /> <span className="text-xs uppercase tracking-wide">{label}</span></div>
      <div className="mt-1 font-display text-3xl font-semibold">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [spas, setSpas] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (checking) return;
    if (!user) navigate("/admin/login");
  }, [user, checking, navigate]);

  const load = async () => {
    try {
      const [l, s, st] = await Promise.all([api.get("/admin/leads"), api.get("/admin/spas"), api.get("/admin/stats")]);
      setLeads(l.data); setSpas(s.data); setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Lead updated"); load();
    } catch (e) { toast.error("Could not update lead"); }
  };

  const saveSpa = async () => {
    const s = editing;
    if (!s.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      // Preserve ALL existing fields (colour configurator, gallery, placement, etc.)
      const payload = {
        ...s,
        seats: s.seats === "" || s.seats == null ? null : parseInt(s.seats, 10),
        sort_order: parseInt(s.sort_order || 0, 10),
        specs: (s.specs || []).filter((sp) => sp.label && sp.value),
        colours: (s.colours || []).filter((c) => c.name),
        upgrade_packs: (s.upgrade_packs || []).filter((u) => u.name),
        features: (s.features || []).filter(Boolean),
        active: s.active !== false,
      };
      delete payload.id;
      delete payload.created_at;
      if (s.id) await api.put(`/admin/spas/${s.id}`, payload);
      else await api.post("/admin/spas", payload);
      toast.success(s.id ? "Model updated" : "Model added");
      setEditing(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("image_url", r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
  };

  const deleteSpa = async (id) => {
    try { await api.delete(`/admin/spas/${id}`); toast.success("Model removed"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setRow = (key, i, k, v) => setEditing((e) => {
    const arr = [...(e[key] || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...e, [key]: arr };
  });
  const addRow = (key, obj) => setEditing((e) => ({ ...e, [key]: [...(e[key] || []), obj] }));

  if (checking || !user) {
    return <div className="flex min-h-screen items-center justify-center bg-[#16171c] text-slate-400"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#16171c] text-white">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-black/60 px-4 py-3 backdrop-blur-xl sm:px-6">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-300"><Waves size={18} /></span>
          <div className="font-display text-sm font-semibold tracking-tight sm:text-base">Showroom Admin</div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/social" data-testid="admin-social-link" className="hidden items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/10 sm:flex">
            Social Pack
          </a>
          <Button onClick={() => { logout(); navigate("/admin/login"); }} variant="outline" className="gap-2 rounded-full border-white/15 bg-white/5 text-sm hover:bg-white/10" data-testid={ADMIN.logout}>
            <LogOut size={15} /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard icon={Inbox} label="Total leads" value={stats.total_leads ?? 0} />
          <StatCard icon={Inbox} label="New" value={stats.new_leads ?? 0} />
          <StatCard icon={Phone} label="Call backs" value={stats.callbacks ?? 0} />
          <StatCard icon={LayoutGrid} label="Active models" value={stats.active_spas ?? 0} />
        </div>

        <div className="mt-6 flex gap-2 border-b border-white/10">
          <button onClick={() => setTab("leads")} data-testid={ADMIN.tabLeads} className={`px-4 py-2.5 text-sm font-medium transition-colors ${tab === "leads" ? "border-b-2 border-cyan-400 text-white" : "text-slate-400 hover:text-white"}`}>Enquiries</button>
          <button onClick={() => setTab("spas")} data-testid={ADMIN.tabSpas} className={`px-4 py-2.5 text-sm font-medium transition-colors ${tab === "spas" ? "border-b-2 border-cyan-400 text-white" : "text-slate-400 hover:text-white"}`}>Catalogue</button>
        </div>

        {tab === "leads" ? (
          <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-wide text-slate-400">
                <tr><th className="p-3">Type</th><th className="p-3">Name</th><th className="p-3">Model / Room</th><th className="p-3">Contact</th><th className="p-3">Received</th><th className="p-3">Status</th></tr>
              </thead>
              <tbody>
                {leads.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No enquiries yet.</td></tr>}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-white/5" data-testid={`${ADMIN.leadRowPrefix}${l.id}`}>
                    <td className="p-3"><span className="rounded-full bg-cyan-500/15 px-2 py-0.5 text-xs text-cyan-300">{CTA_LABEL[l.cta_type] || l.cta_type}</span></td>
                    <td className="p-3 font-medium">{l.name}</td>
                    <td className="p-3 text-slate-400">{l.spa_name || "—"}<br /><span className="text-xs text-slate-500">{l.room}</span></td>
                    <td className="p-3 text-slate-300">{l.phone || ""}<br /><span className="text-xs text-slate-500">{l.email || ""}</span></td>
                    <td className="p-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      <select value={l.status} onChange={(e) => updateLeadStatus(l.id, e.target.value)} data-testid={`${ADMIN.leadStatusPrefix}${l.id}`} className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs capitalize outline-none focus:ring-1 focus:ring-cyan-500">
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-[#1d1f26]">{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4">
            <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-cyan-400/20 bg-cyan-500/[0.06] p-3.5 text-sm text-slate-300" data-testid="admin-help-note">
              <Info size={16} className="mt-0.5 shrink-0 text-cyan-300" />
              <div>
                <span className="font-semibold text-cyan-200">Editing tips —</span> Tap <span className="text-white">Add model</span> for a new product, or <span className="text-white">Edit</span> on any card to change it.
                Use <span className="text-white">Upload image from computer</span> to swap a photo, and the <span className="text-white">Specifications</span> section to add or fix spec rows (label + value).
                Your changes go live in the showroom the moment you hit <span className="text-white">Save</span>. Colour configurator, gallery and backyard images are kept safe when you edit.
              </div>
            </div>
            <div className="mb-3 flex justify-end">
              <Button onClick={() => setEditing({ ...emptySpa })} className="gap-2 rounded-full bg-cyan-500 text-black hover:bg-cyan-400" data-testid={ADMIN.addSpa}><Plus size={16} /> Add model</Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {spas.map((s) => (
                <div key={s.id} className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
                  <div className="relative h-36 bg-white/5">
                    {s.image_url ? <img src={mediaUrl(s.image_url)} alt={s.name} className="h-full w-full object-contain p-2" /> : null}
                    {!s.active && <span className="absolute left-2 top-2 rounded bg-black/70 px-2 py-0.5 text-xs text-amber-300">Retired</span>}
                    <span className="absolute right-2 top-2 rounded bg-black/60 px-2 py-0.5 text-[10px] text-cyan-300">{CAT_LABEL[s.category]}</span>
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-wide text-amber-400">{s.brand}</div>
                    <div className="mt-0.5 font-display font-semibold">{s.name}</div>
                    <p className="mt-0.5 text-xs text-slate-400">{[s.seats ? `${s.seats} seats` : null, s.size_label].filter(Boolean).join(" · ")}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing({ ...s, seats: s.seats ?? "" })} className="flex-1 gap-1.5 rounded-full border-white/15 bg-white/5 text-xs hover:bg-white/10" data-testid={`${ADMIN.spaEditPrefix}${s.id}`}><Pencil size={13} /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteSpa(s.id)} className="gap-1.5 rounded-full border-red-500/20 bg-red-500/10 text-xs text-red-300 hover:bg-red-500/20" data-testid={`${ADMIN.spaDeletePrefix}${s.id}`}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ type: "spring", stiffness: 240, damping: 28 }} className="relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-white/10 bg-[#191b21] p-6 no-scrollbar" data-testid={ADMIN.spaForm}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">{editing.id ? "Edit model" : "Add model"}</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-white" data-testid={ADMIN.spaCancel}><X size={20} /></button>
            </div>
            <div className="mt-4 space-y-3">
              <Input placeholder="Name" value={editing.name} onChange={(e) => setField("name", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaName} />
              <div className="grid grid-cols-2 gap-2">
                <select value={editing.category} onChange={(e) => setField("category", e.target.value)} className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500" data-testid={ADMIN.spaCategory}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value} className="bg-[#1d1f26]">{c.label}</option>)}
                </select>
                <Input placeholder="Brand" value={editing.brand} onChange={(e) => setField("brand", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaBrand} />
              </div>
              <Input placeholder="Tagline" value={editing.tagline} onChange={(e) => setField("tagline", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaTagline} />
              <Textarea placeholder="Description" value={editing.description} onChange={(e) => setField("description", e.target.value)} className="min-h-[90px] border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaDescription} />
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="Seats" value={editing.seats} onChange={(e) => setField("seats", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaSeats} />
                <Input placeholder="Size label (e.g. 6m)" value={editing.size_label} onChange={(e) => setField("size_label", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaSize} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Dimensions" value={editing.dimensions} onChange={(e) => setField("dimensions", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" />
                <Input placeholder="Zone (swim spa)" value={editing.zone} onChange={(e) => setField("zone", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Swim system" value={editing.swim_system} onChange={(e) => setField("swim_system", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" />
                <Input placeholder="Config (urban pool)" value={editing.config} onChange={(e) => setField("config", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" />
              </div>
              <Input placeholder="Image URL" value={editing.image_url} onChange={(e) => setField("image_url", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={ADMIN.spaImage} />
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-white/20 bg-white/[0.03] px-3 py-2 text-xs text-slate-300 hover:bg-white/5" data-testid="admin-spa-upload-label">
                <Plus size={13} /> Upload image from computer
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="admin-spa-upload" />
              </label>
              {editing.image_url ? <img src={mediaUrl(editing.image_url)} alt="preview" className="h-32 w-full rounded-lg object-contain bg-white/5" /> : null}

              <FieldList title="Specifications" items={editing.specs || []} onAdd={() => addRow("specs", { label: "", value: "" })} render={(sp, i) => (
                <div className="flex gap-2" data-testid={`admin-spa-spec-${i}`}>
                  <Input value={sp.label} onChange={(e) => setRow("specs", i, "label", e.target.value)} placeholder="Label (e.g. Total jets)" className="border-white/10 bg-white/5 text-sm focus-visible:ring-cyan-500" />
                  <Input value={sp.value} onChange={(e) => setRow("specs", i, "value", e.target.value)} placeholder="Value (e.g. 45 jets)" className="border-white/10 bg-white/5 text-sm focus-visible:ring-cyan-500" />
                </div>
              )} />

              <FieldList title="Features" items={editing.features || []} onAdd={() => addRow("features", "")} render={(f, i) => (
                <Input value={f} onChange={(e) => setEditing((ed) => { const arr = [...ed.features]; arr[i] = e.target.value; return { ...ed, features: arr }; })} placeholder="Feature" className="border-white/10 bg-white/5 text-sm focus-visible:ring-cyan-500" />
              )} />

              <FieldList title="Colour options" items={editing.colours || []} onAdd={() => addRow("colours", { name: "", kind: "shell", image_url: "" })} render={(c, i) => (
                <div className="flex gap-2">
                  <Input value={c.name} onChange={(e) => setRow("colours", i, "name", e.target.value)} placeholder="Name" className="border-white/10 bg-white/5 text-sm focus-visible:ring-cyan-500" />
                  <select value={c.kind} onChange={(e) => setRow("colours", i, "kind", e.target.value)} className="rounded-md border border-white/10 bg-white/5 px-2 text-sm outline-none">
                    <option value="shell" className="bg-[#1d1f26]">shell</option><option value="cabinet" className="bg-[#1d1f26]">cabinet</option>
                  </select>
                </div>
              )} />

              <FieldList title="Upgrade packs" items={editing.upgrade_packs || []} onAdd={() => addRow("upgrade_packs", { name: "", description: "" })} render={(u, i) => (
                <div className="space-y-1">
                  <Input value={u.name} onChange={(e) => setRow("upgrade_packs", i, "name", e.target.value)} placeholder="Pack name" className="border-white/10 bg-white/5 text-sm focus-visible:ring-cyan-500" />
                  <Input value={u.description} onChange={(e) => setRow("upgrade_packs", i, "description", e.target.value)} placeholder="Description" className="border-white/10 bg-white/5 text-sm focus-visible:ring-cyan-500" />
                </div>
              )} />

              <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="Sort order" value={editing.sort_order} onChange={(e) => setField("sort_order", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" />
                <Input placeholder="Source URL" value={editing.source_url} onChange={(e) => setField("source_url", e.target.value)} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" />
              </div>

              <label className="flex items-center gap-2 pt-1 text-sm text-slate-300">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setField("active", e.target.checked)} className="h-4 w-4 accent-cyan-500" data-testid={ADMIN.spaActive} />
                Active (visible in showroom & range)
              </label>

              <Button onClick={saveSpa} disabled={saving} className="mt-3 w-full rounded-full bg-cyan-500 py-6 font-semibold text-black hover:bg-cyan-400" data-testid={ADMIN.spaSave}>
                {saving ? <Loader2 className="animate-spin" size={18} /> : editing.id ? "Save changes" : "Add model"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function FieldList({ title, items, onAdd, render }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-400">{title}</span>
        <button onClick={onAdd} className="text-xs text-cyan-400 hover:text-cyan-300">+ Add</button>
      </div>
      <div className="space-y-2">{items.map((it, i) => <div key={i}>{render(it, i)}</div>)}</div>
    </div>
  );
}
